import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import { EmptyState } from '@monorepo/shared/componentsV2/EmptyState';
import { getDocumentFileColumns } from '@monorepo/shared/componentsV2/documents/getDocumentFileColumns';
import { Pagination } from '@monorepo/shared/componentsV2/Table/Pagination';
import { Table } from '@monorepo/shared/componentsV2/Table/Table';
import { useFrontendPagination } from '@monorepo/shared/hooks/useFrontendPagination';
import { useHasOrganizationUpdatePermissions } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { getDocumentUploadEmptyStateSecondaryText } from '@monorepo/shared/utils/documentUpload';
import { lookFor } from '@monorepo/shared/utils/lookFor';
import { DocumentFileResponse } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useOrganizationDocumentsList } from '../../hooks/useOrganizationDocumentsList';
import { StyledMain } from '../styled';
import { FolderDocumentsTableActions } from './FolderDocumentsTableActions';

const StyledTable = styled(Table)`
  height: 100%;
  padding-bottom: 4rem;
` as typeof Table;

interface FolderDocumentsTableProps {
  organizationId: string;
  folderId: string;
  filter: string;
}

export function FolderDocumentsTable({
  organizationId,
  folderId,
  filter,
}: FolderDocumentsTableProps) {
  const { documents, isLoading } = useOrganizationDocumentsList({
    organizationId,
    folderId,
  });
  const canEditOrgSettings =
    useHasOrganizationUpdatePermissions(organizationId);

  const columns = useMemo(
    () =>
      getDocumentFileColumns({
        actions: canEditOrgSettings
          ? (document) => (
              <FolderDocumentsTableActions
                organizationId={organizationId}
                document={document}
              />
            )
          : undefined,
      }),
    [canEditOrgSettings, organizationId],
  );

  const filterRowsBy = useCallback(
    (row: DocumentFileResponse) => lookFor(filter, row, ['name']),
    [filter],
  );

  const { page, setPage, filteredResults, rows, rowsPerPage } =
    useFrontendPagination({
      items: documents,
      filterRowsBy,
      resetPageWhen: [folderId, filter],
    });

  const { SkipLink, SkipLinkTarget } = useSkipWidgetLink();

  if (isLoading) {
    return <Loading />;
  }

  return (
    <StyledMain>
      {documents?.length ? (
        <>
          <SkipLink linkText="Skip to pagination" />
          <StyledTable
            ariaLabel="Folder documents"
            columns={columns}
            rows={rows}
            isPrefilteredAndNotEmpty
            makeTableFocusable
          />
          <SkipLinkTarget />
          <Pagination
            totalRowCount={filteredResults.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
          />
        </>
      ) : (
        <EmptyState
          primaryText="There are no documents in this folder yet."
          secondaryText={
            canEditOrgSettings
              ? getDocumentUploadEmptyStateSecondaryText({
                  uploadButtonLabel: 'Add documents',
                })
              : 'Your organization admin can add documents to this folder.'
          }
        />
      )}
    </StyledMain>
  );
}
